/**
 * `<MultiSelectField>` — a dropdown of grouped checkboxes with select-all
 * group headers, for `ActionModal`'s `multiselect` field kind.
 *
 * Composed from the SPA's proven pieces rather than invented: the scrolling
 * checkbox-menu shape is `/data`'s add-field menu (`.data-add-field-menu`),
 * the outside-click + Escape close is `PopMenu`'s, and the group select-all
 * algebra is `TierToolChecklist`'s (`StudioConfig`). A dropdown PANEL, not an
 * inline list, because `.modal-card` is 380px with no max-height — a catalog
 * inlined into the form would push the modal past the viewport.
 *
 * Value contract (the `ActionModal` `FieldValue` string): a JSON-encoded
 * string array when anything is selected, and the EMPTY STRING when nothing
 * is — the empty string is what lets `ActionModal`'s existing
 * omit-empty-optional / required logic work untouched. Never `"[]"`.
 *
 * A group header's checkbox ticks/unticks every option in the group
 * (indeterminate when partially selected), so "Packages and memberships"
 * stays a two-click selection while any single item stays deselectable.
 */

import { useEffect, useRef, useState } from "react";

export interface MultiSelectOption {
  readonly value: string;
  readonly label: string;
}

export interface MultiSelectGroup {
  readonly label: string;
  readonly options: ReadonlyArray<MultiSelectOption>;
}

/** Parse the field value ("" or a JSON string array) into a Set. */
export function parseMultiSelectValue(value: string): ReadonlySet<string> {
  if (value === "") return new Set();
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((v): v is string => typeof v === "string"));
    }
  } catch {
    // A malformed value reads as empty — the field re-emits a clean one on
    // the next interaction.
  }
  return new Set();
}

function encode(selected: ReadonlySet<string>): string {
  return selected.size === 0 ? "" : JSON.stringify([...selected].sort());
}

export function MultiSelectField({
  groups,
  value,
  onChange,
  placeholder,
}: {
  readonly groups: ReadonlyArray<MultiSelectGroup>;
  /** ActionModal's field value — "" or a JSON-encoded string array. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const selected = parseMultiSelectValue(value);
  const total = groups.reduce((n, g) => n + g.options.length, 0);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleOption = (v: string): void => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(encode(next));
  };

  const toggleGroup = (g: MultiSelectGroup): void => {
    const allOn = g.options.every((o) => selected.has(o.value));
    const next = new Set(selected);
    for (const o of g.options) {
      if (allOn) next.delete(o.value);
      else next.add(o.value);
    }
    onChange(encode(next));
  };

  const summary =
    selected.size === 0
      ? placeholder ?? "Pick items…"
      : selected.size === total
        ? "Everything"
        : groups
            .map((g) => {
              const n = g.options.filter((o) => selected.has(o.value)).length;
              if (n === 0) return null;
              const all = n === g.options.length ? "all " : "";
              return `${all}${n}/${g.options.length} ${g.label.toLowerCase()}`;
            })
            .filter((s): s is string => s !== null)
            .join(" · ");

  return (
    <div className="multi-select" ref={ref}>
      <button
        type="button"
        className="multi-select-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="multi-select-trigger-label">{summary}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div className="multi-select-menu" role="listbox" aria-multiselectable>
          {groups.map((g) => {
            const onCount = g.options.filter((o) =>
              selected.has(o.value),
            ).length;
            const allOn = onCount === g.options.length && g.options.length > 0;
            return (
              <div key={g.label}>
                <label className="multi-select-group-head">
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={(el) => {
                      // Partial group → indeterminate; only settable via DOM.
                      if (el !== null) {
                        el.indeterminate = onCount > 0 && !allOn;
                      }
                    }}
                    onChange={() => toggleGroup(g)}
                  />
                  <span>{g.label}</span>
                </label>
                {g.options.map((o) => (
                  <label key={o.value} className="multi-select-option">
                    <input
                      type="checkbox"
                      checked={selected.has(o.value)}
                      onChange={() => toggleOption(o.value)}
                    />
                    <span>{o.label}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
